import { useEffect, useState } from "react";

import Link from "next/link";

import classNames from "classnames/bind";
import useMediaQuery from "hooks/useMediaQuery/useMediaQuery";
import { ResolvedMediaContentFileURL } from "resolvers/data/mediaContentFileURLResolver";
import { ResolvedMediaContentRow } from "resolvers/data/mediaContentRowResolver";
import { ResolvedMediaContentTable } from "resolvers/data/mediaContentTableResolver";
import dayjs from "utils/dayjs";
import { z } from "zod";

import { GenericPagination } from "../GenericPagination";
import UIIcon from "../UIIcon";
import styles from "./MediaContentTable.module.scss";

const cx = classNames.bind(styles);

const URLSchema = z.string().url();
const VideoExtensionsSchema = z.enum(["mp4", "avi", "mov", "wvm", "flv", "avchd", "webm", "mkv"]);
const ZipExtenstionSchema = z.literal("zip");
const PDFExtenstionSchema = z.literal("pdf");

export const MediaContentTable = (props: ResolvedMediaContentTable) => {
    const [currentItems, setCurrentItems] = useState<ResolvedMediaContentRow[]>([]);

    const { isBelowTabletLarge } = useMediaQuery();

    useEffect(() => {
        setCurrentItems(props.contentRows);
    }, [props.contentRows]);

    const renderAttachments = (filesURL: ResolvedMediaContentFileURL[]) => {
        return filesURL.map((u, i) => {
            const isURLValid = URLSchema.safeParse(u.url);

            if (!isURLValid.success) {
                console.error(isURLValid.error.flatten().fieldErrors);
                return;
            }

            const url = new URL(isURLValid.data);

            const extension = url.pathname.split(".")[1];

            const isPDF = PDFExtenstionSchema.safeParse(extension);
            const isZip = ZipExtenstionSchema.safeParse(extension);
            const isVideo = VideoExtensionsSchema.safeParse(extension);

            let action: { label: string; icon: JSX.Element } = {
                label: "View File",
                icon: UIIcon({ icon: "File" }),
            };

            if (isPDF.success) {
                action = {
                    label: "Download PDF",
                    icon: UIIcon({ icon: "Pdf" }),
                };
            } else if (isZip.success) {
                action = {
                    label: "Download ZIP",
                    icon: UIIcon({ icon: "Zip" }),
                };
            } else if (isVideo.success || url.host.includes("vimeo.com")) {
                action = {
                    label: "Watch Video",
                    icon: UIIcon({ icon: "Video" }),
                };
            }

            return (
                <Link key={i} href={u.url} target="_blank" className={cx("action")}>
                    {action.icon}
                    {action.label}
                </Link>
            );
        });
    };

    const handleContentChange = (e: ResolvedMediaContentRow[]) => {
        setCurrentItems([...e]);
    };

    return (
        <>
            <div className={cx("mediaContentTable")}>
                {isBelowTabletLarge ? (
                    currentItems.map((c, i) => (
                        <>
                            <div key={i} className={cx("mediaContentTable-mobile")}>
                                <div className={cx("mediaContentTable-mobile--heading")}>
                                    <h3 className={cx("mediaContentTable-mobile--head")}>
                                        {c.documentTitle}
                                    </h3>
                                    <p className={cx("mediaContentTable-mobile--uploadDate")}>
                                        <span>Uploaded: </span>
                                        {dayjs(c.uploadFileDate).format("DD MMMM YYYY")}
                                    </p>
                                </div>
                                <div className={cx("mediaContentTable-mobile--callToAction")}>
                                    {renderAttachments(c.filesUrl)}
                                </div>
                            </div>
                            <div className={cx("mediaContentTable-mobile--separator")}></div>
                        </>
                    ))
                ) : (
                    <table className={cx("mediaContentTable-desktop")}>
                        <tbody>
                            <tr className={cx("mediaContentTable-desktop--head")}>
                                <th>{props.titleColumnHeader}</th>
                                <th>{props.dateColumnHeader}</th>
                                <th>{props.actionColumnHeader}</th>
                            </tr>
                            {currentItems.map((c, i) => (
                                <>
                                    <tr
                                        key={i}
                                        className={cx("mediaContentTable-desktop--content")}
                                    >
                                        <td>{c.documentTitle}</td>
                                        <td>{dayjs(c.uploadFileDate).format("DD MMMM YYYY")}</td>
                                        <td>{renderAttachments(c.filesUrl)}</td>
                                    </tr>
                                </>
                            ))}
                        </tbody>
                    </table>
                )}
                <GenericPagination<ResolvedMediaContentRow>
                    items={props.contentRows}
                    itemsPerPage={10}
                    CurrentPageItemsEvent={handleContentChange}
                />
            </div>
        </>
    );
};
